import { type Directive, type DirectiveBinding } from "vue";

const getRelativeTime = (timestamp: number): string => {
  const epochs: [string, number][] = [
    ["year", 31536000],
    ["month", 2592000],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
    ["second", 1]
  ];

  const timeDiffInSeconds = Math.floor(
    (new Date().getTime() - new Date(timestamp).getTime()) / 1000
  );
  for (const [name, seconds] of epochs) {
    const interval = Math.floor(Math.abs(timeDiffInSeconds) / seconds);
    if (interval >= 1) {
      const suffix = interval === 1 ? "" : "s";
      if (timeDiffInSeconds >= 0) {
        // past
        return `${interval} ${name}${suffix} ago`;
      } else {
        // future
        return `In ${interval} ${name}${suffix}`;
      }
    }
  }
  return "Just now";
};

const updateRelativeTime = (
  el: HTMLElementWithObserver,
  timestamp: DirectiveBinding<number>
) => {
  el.textContent = getRelativeTime(timestamp.value);
};

interface HTMLElementWithObserver extends HTMLElement {
  _relativeTimeObserver?: MutationObserver;
  _relativeTimeInterval?: number;
}

export const relativeTimeDirective: Directive = {
  updated(el, binding) {
    // Update the relative time when only the binding value changes
    updateRelativeTime(el, binding);
  },
  mounted(el, binding) {
    if (typeof binding.value !== "number") {
      console.warn(
        "[v-relative-time] Value must be a timestamp in milliseconds"
      );
      return;
    }

    // Update immediately
    updateRelativeTime(el, binding);

    // Watch for changes in the timestamp and automatically update
    el._relativeTimeObserver = new MutationObserver(() => {
      updateRelativeTime(el, binding);
    });
    el._relativeTimeObserver.observe(el, {
      characterData: true,
      subtree: true
    });

    // Periodically update the time (e.g., every minute)
    el._relativeTimeInterval = window.setInterval(
      () => updateRelativeTime(el, binding),
      60000
    );
  },
  beforeUnmount(el: HTMLElementWithObserver) {
    // Clear the interval if it was set
    if (el._relativeTimeInterval) {
      clearInterval(el._relativeTimeInterval);
    }
    // Disconnect the observer if it was set
    if (el._relativeTimeObserver) {
      el._relativeTimeObserver.disconnect();
    }
  }
};
